import { systemPrompt } from '../prompt';
import { unverifiedNumbers } from '../provenance';
import { TOOL_SPECS, type TakeoffResult } from '../tools';
import { effectsFrom, highlightsFrom, suggestFrom, traceLine } from '../trace';
import type { Answer, AskContext, Engine, ToolCall, ToolSpec } from '../types';
import { matchesBuild, route, takeoffText } from './keyword';
import { lfm2Prompt, parseToolCalls, stripToolTokens, type ChatMessage } from './lfm2';
import { renderLast } from './render';

export interface Generator {
  generate(prompt: string, opts?: { maxNewTokens?: number; signal?: AbortSignal }): Promise<string>;
}

// What the model's tool calls run against: the in-page MCP client by default, a trivial wrapper
// over makeTools in tests, or a stdio/remote MCP client elsewhere.
export interface Executor {
  specs: ToolSpec[];
  call(name: string, args: Record<string, unknown>): Promise<unknown>;
}

export interface LocalOptions {
  generator: Generator;
  executor?: Executor;
  maxSteps?: number;
  maxNewTokens?: number;
  signal?: AbortSignal;
}

// The model answers questions: it picks tools, and if its final text quotes numbers no tool
// returned, the text is replaced by a deterministic rendering of the last tool result. Every
// change request goes to the rules before the model sees it, and build is not among its tools.
export function localEngine(opts: LocalOptions): Engine {
  const maxSteps = opts.maxSteps ?? 4;
  return {
    name: 'local',
    async answer(question, ctx: AskContext): Promise<Answer> {
      if (matchesBuild(question, ctx)) return route(question, ctx);
      const executor = opts.executor ?? (await defaultExecutor(ctx));
      const specs = executor.specs.filter((t) => t.name !== 'build');
      const calls: ToolCall[] = [];
      const raw: string[] = [];
      const messages: ChatMessage[] = [
        { role: 'system', content: questionsOnly(systemPrompt(ctx.heroName)) + LOCAL_HINT },
        ...(ctx.history ?? []).slice(-3).flatMap((t): ChatMessage[] => [
          { role: 'user', content: t.question },
          { role: 'assistant', content: t.answer },
        ]),
        { role: 'user', content: question },
      ];
      const de = /[äöüß]|wie viel|welche|gibt es|pro geschoss|kubik/i.test(question);
      const finish = (reply: string): Answer => {
        let text = stripToolTokens(reply);
        let unverified = unverifiedNumbers(text, calls);
        if (unverified.length > 0 || !text) {
          const takeoff = lastTakeoff(calls);
          const rendered = takeoff ? takeoffText(takeoff, de) : renderLast(calls, de);
          if (rendered) {
            text = rendered;
            unverified = [];
          }
        }
        if (!text) text = fallbackText(calls, de);
        return {
          text,
          highlights: highlightsFrom(calls),
          trace: calls.map(traceLine),
          calls,
          via: 'local',
          unverified,
          raw,
          effects: effectsFrom(calls),
          suggest: suggestFrom(calls),
        };
      };

      for (let step = 0; step < maxSteps; step++) {
        const reply = await opts.generator.generate(lfm2Prompt(messages, specs), {
          maxNewTokens: opts.maxNewTokens ?? 256,
          signal: opts.signal,
        });
        raw.push(reply);
        const parsed = parseToolCalls(reply);
        if (parsed.length === 0) return finish(reply);
        messages.push({ role: 'assistant', content: reply.trim() });
        const results = [];
        for (const c of parsed)
          results.push(
            c.name === 'build'
              ? { error: 'changes run through the build commands, not the model' }
              : await callViaExecutor(executor, c.name, c.args, calls),
          );
        messages.push({
          role: 'tool',
          content: JSON.stringify(results.map(trimForModel)),
        });
      }
      return finish('');
    },
  };
}

const LOCAL_HINT =
  '\nCall at most one tool per turn. After the tool result, answer in one or two sentences using only the numbers in the result.';

// the prompt without its build paragraph, which the MCP side keeps
const questionsOnly = (prompt: string) => prompt.replace(/\n\nChanging the building:[^]*$/, '');

// Built once per question, over the in-memory MCP transport, so the local engine drives the
// exact same tool surface an external MCP client (stdio.ts) sees.
async function defaultExecutor(ctx: AskContext): Promise<Executor> {
  const { inPageClient } = await import('../mcp/client');
  const client = inPageClient(ctx);
  return { specs: TOOL_SPECS, call: (name, args) => client.callTool(name, args) };
}

async function callViaExecutor(
  executor: Executor,
  name: string,
  input: Record<string, unknown>,
  calls: ToolCall[],
): Promise<unknown> {
  let output: unknown;
  try {
    output = await executor.call(name, input);
  } catch (err) {
    output = { error: (err as Error)?.message ?? String(err) };
  }
  calls.push({ tool: name, input, output });
  return output;
}

function lastTakeoff(calls: ToolCall[]): TakeoffResult | null {
  for (let i = calls.length - 1; i >= 0; i--)
    if (calls[i]!.tool === 'quantity_takeoff') return calls[i]!.output as TakeoffResult;
  return null;
}

function trimForModel(out: unknown): unknown {
  if (!out || typeof out !== 'object') return out;
  const o = { ...(out as Record<string, unknown>) };
  if (o.bytes instanceof Uint8Array) o.bytes = `[${o.bytes.length} bytes]`;
  if (o.attachment) o.attachment = `[${(o.attachment as { size: number }).size} bytes]`;
  if (Array.isArray(o.sheets))
    o.sheets = (o.sheets as { name: string; rows: unknown[]; totals: unknown[] }[]).map((s) => ({
      name: s.name,
      rows: s.rows.length,
      totals: s.totals,
    }));
  if (Array.isArray(o.ids)) o.ids = `[${o.ids.length} ids]`;
  if (Array.isArray(o.elements)) o.elements = (o.elements as unknown[]).slice(0, 12);
  return o;
}

function fallbackText(calls: ToolCall[], de: boolean): string {
  const last = calls.at(-1);
  if (!last) return de ? 'Keine Antwort vom Modell.' : 'No answer from the model.';
  return (de ? 'Ergebnis: ' : 'Result: ') + traceLine(last);
}
